"use client";

import { useState } from "react";
import { useDatadog } from "@auth/client-sdk-react";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import type {
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";
import { useTranslations } from "next-intl";

import { useAPI } from "apps/website/hooks/useAPI";

import { SYSTEM_ERROR_MESSAGES } from "./constants";
import { CreateBasketResult } from "./types";

import { CheckoutStatus } from "@/primary-models";

/** Handler for `<ExpressCheckoutElement>`'s `onConfirm` event.
 *
 * Takes a `createBasket` function (resolved by the version-specific wrapper —
 * `<PayPalExpressCheckoutV1>` or `<PayPalExpressCheckoutV2>`) so this hook
 * stays agnostic of which back-end produced the basket. It asks the
 * caller-provided `createBasket()` for a Stripe `clientSecret`, then calls
 * `stripe.confirmPayment(...)` so the buyer can complete the PayPal
 * authorisation. Once Stripe confirms, it notifies the back-end via
 * `POST /payments/basket-confirmed` and only then calls the parent
 * `onSuccess` callback (which navigates to the post-checkout success page).
 * This mirrors the V1 card flow's `handleNewBasket` path so PayPal completes
 * the same lifecycle as the card path.
 *
 * Errors that happen *after* the user clicks "Pay" in the PayPal sheet are
 * surfaced two ways:
 *
 *   - `event.paymentFailed({ reason, message })` resets the Express Checkout
 *     element so the buyer can retry. Stripe doesn't reliably show the message
 *     to the buyer in our PayPal popup flow (the popup is already gone by the
 *     time `onConfirm` runs), so this on its own isn't enough.
 *   - `onError(message)` is the Express Checkout equivalent of V1's
 *     `handleError(...)` — it bubbles a user-facing message up to the page so
 *     the host component can render a `<MessagingCard>` banner (mirroring
 *     `<DefaultCheckout>`'s error UI). The raw technical message stays in
 *     Datadog; only friendly copy is forwarded to `onError`. */
export function useConfirmPayment({
  createBasket,
  onSuccess,
  onError,
}: {
  createBasket: () => Promise<CreateBasketResult>;
  onSuccess: () => void;
  onError: (message: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const datadog = useDatadog();
  const { Checkout: checkout } = useAPI();
  const t = useTranslations("Shared.PayPalExpressCheckout.Errors");

  // Drives the processing modal in `<PayPalExpressCheckoutInner>`. Flipped on
  // *after* `stripe.confirmPayment` resolves successfully — the buyer's money
  // is committed at that point and the only remaining work is our own
  // back-end basket-confirmed call. Without this, that gap is silent and
  // buyers retry their payment thinking nothing happened. Reset on every
  // post-confirm failure branch so the modal yields to the error banner.
  const [ isProcessing, setIsProcessing ] = useState(false);

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      datadog.logger.error(SYSTEM_ERROR_MESSAGES.MISSING_STRIPE_OR_ELEMENTS, {
        eventName: "ExpressCheckoutMissingStripeOrElements",
      });
      onError(t("genericPaymentFailed"));
      return;
    }

    const result = await createBasket();

    if (!result.success) {
      const userErrorMessage = result.message ?? t("genericPaymentFailed");
      datadog.logger.error(SYSTEM_ERROR_MESSAGES.createBasketFailed(result.message), {
        eventName: "ExpressCheckoutCreateBasketFailed",
        failedOn: result.failedOn,
      });
      event.paymentFailed({ reason: "fail", message: userErrorMessage });
      onError(userErrorMessage);
      return;
    }

    // The forms-actions back-end returns `existingBasket` when a previous
    // attempt is still being processed. We don't have a `clientSecret` to
    // re-confirm with in this case, so for now we surface a clear error and
    // bail out. Resuming an existing basket can be handled later.
    if (!result.intentClientSecret) {
      datadog.logger.error(SYSTEM_ERROR_MESSAGES.EXISTING_BASKET, {
        eventName: "ExpressCheckoutExistingBasket",
        existingBasket: result.existingBasket,
        isProcessed: result.isProcessed,
      });
      event.paymentFailed({ reason: "fail", message: t("checkoutInProgress") });
      onError(t("checkoutInProgress"));
      return;
    }

    const { basketId, intentClientSecret } = result;
    const returnUrl = globalThis.location?.href;

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: intentClientSecret,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: "if_required",
    });

    if (error) {
      const userErrorMessage = error.message ?? t("genericPaymentFailed");
      datadog.logger.error(SYSTEM_ERROR_MESSAGES.CONFIRM_PAYMENT_FAILED, {
        eventName: "ExpressCheckoutConfirmPaymentFailed",
        stripeError: error,
        basketId,
      });

      event.paymentFailed({ reason: "fail", message: userErrorMessage });
      onError(userErrorMessage);
      return;
    }

    // Stripe has the PayPal authorisation. From here onwards the buyer's
    // money is committed and only our own back-end work is left, so the
    // processing modal takes over so they don't retry the payment.
    setIsProcessing(true);

    let confirmation;
    try {
      confirmation = await checkout.postPaymentsBasketConfirmed({ basketId });
    } catch (basketConfirmationError) {
      datadog.logger.error(SYSTEM_ERROR_MESSAGES.basketConfirmedRequestFailed(basketId), {
        eventName: "ExpressCheckoutBasketConfirmedRequestFailed",
        basketId,
        basketConfirmationError,
      });
      setIsProcessing(false);
      event.paymentFailed({ reason: "fail", message: t("genericPaymentFailed") });
      onError(t("genericPaymentFailed"));
      return;
    }

    if (confirmation?.status !== CheckoutStatus.SUCCESS) {
      datadog.logger.error(
        SYSTEM_ERROR_MESSAGES.basketConfirmedUnsuccessful(basketId, confirmation.status),
        {
          eventName: "ExpressCheckoutBasketConfirmedUnsuccessful",
          basketId,
          confirmation,
        },
      );
      setIsProcessing(false);
      event.paymentFailed({ reason: "fail", message: t("genericPaymentFailed") });
      onError(t("genericPaymentFailed"));
      return;
    }

    datadog.logger.info(`ExpressCheckout: basket '${basketId}' confirmed successfully, navigating to success page`, {
      eventName: "ExpressCheckoutBasketConfirmedSuccessful",
      basketId,
      confirmation,
    });

    // Leave `isProcessing` true: `onSuccess` triggers a full-page navigation
    // away, so the modal stays up until the new page mounts (rather than
    // flashing a "no modal" frame between the navigate call and the new
    // page render).
    onSuccess();

    return { success: true };
  };

  return { handleConfirm, isProcessing };
}
